import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FormikErrors, FormikValues } from 'formik';
import { AppRootState } from '@reduxStore';

interface FormStateEntry {
	dirty: boolean;
	isValid: boolean;
	isSubmitting: boolean;
	errors: FormikErrors<FormikValues>;
}

interface FormStateSliceState {
	/** Form state for the Leg Formik container */
	leg: FormStateEntry | undefined;
	/** Form state for the Order Formik container */
	order: FormStateEntry | undefined;
	/** Form state for the Notes Formik container */
	notes: FormStateEntry | undefined;
	/** Which form is currently active (determines what the header reads) */
	activeForm: 'leg' | 'order' | 'notes' | undefined;
	/** The HTML form id of the currently active form (for submit button targeting) */
	activeFormId: string | undefined;
}

const initialState: FormStateSliceState = {
	leg: undefined,
	order: undefined,
	notes: undefined,
	activeForm: undefined,
	activeFormId: undefined
};

const formStateSlice = createSlice({
	name: 'consignmentFormState',
	initialState,
	reducers: {
		setLegFormState(state, action: PayloadAction<FormStateEntry>) {
			state.leg = action.payload;
		},
		setOrderFormState(state, action: PayloadAction<FormStateEntry>) {
			state.order = action.payload;
		},
		setNotesFormState(state, action: PayloadAction<FormStateEntry>) {
			state.notes = action.payload;
		},
		setActiveForm(
			state,
			action: PayloadAction<{
				form: 'leg' | 'order' | 'notes' | undefined;
				formId: string | undefined;
			}>
		) {
			state.activeForm = action.payload.form;
			state.activeFormId = action.payload.formId;
		},
		clearFormState() {
			return initialState;
		}
	}
});

export const { setLegFormState, setOrderFormState, setNotesFormState, setActiveForm, clearFormState } = formStateSlice.actions;

/** Select the active form's state for the modal header */
export const selectActiveFormState = (state: AppRootState) => {
	const { activeForm, leg, order, notes } = state[formStateSlice.name];
	if (activeForm === 'order') return order;
	if (activeForm === 'leg') return leg;
	if (activeForm === 'notes') return notes;
	return undefined;
};

/** Select the HTML form id of the currently active form */
export const selectActiveFormId = (state: AppRootState) => state[formStateSlice.name].activeFormId;

export default formStateSlice;
